import React from 'react'
import { connect } from 'react-redux'
import bowser from 'bowser'
import { setStepDebug as SetStepDebug, reRunLastRound, setRestoreState, disableRestoreState,
  toggleHotzone, setGamespeed, setNoFullscreen, toggleFPSmeter, toggleRecordingControls,
  toggleConfigurableVariables, setMenuOpacity, togglePopupTriggerer,
} from '../../redux/actions'

const macGuffinPackage = require('../../package.json')
const inputStyle = { width: '25px', height: '25px', cursor: 'pointer' }

/**
 * The debug sub-category in the DevTools.
 */
@connect(state => ({
  stepDebug: state.base.stepDebug,
  restoreStateDisabled: state.base.restoreStateDisabled,
  sid: state.session.sid,
  userid: state.session.userid,
  response: state.base.response,
  hotzones: state.base.hotzones,
  gamespeed: state.base.gamespeed,
  menuOpacity: state.base.menuOpacity,
  noFullscreen: state.session.noFullscreen,
  hideFPSmeter: state.base.hideFPSmeter,
  showRecordingControls: state.base.showRecordingControls,
  showConfigurableVariables: state.base.showConfigurableVariables,
  showPopupTriggerer: state.base.showPopupTriggerer,
}), {
  setStepDebug: SetStepDebug,
  reRunLastRound,
  doSetRestoreState: setRestoreState,
  disableRestore: disableRestoreState,
  doToggleHotzone: toggleHotzone,
  setGamespeed,
  setMenuOpacity,
  doSetNoFullscreen: setNoFullscreen,
  doToggleFPSmeter: toggleFPSmeter,
  doToggleRecordingControls: toggleRecordingControls,
  doToggleConfigurableVariables: toggleConfigurableVariables,
  doTogglePopupTriggerer: togglePopupTriggerer,
})
export default class DebugPage extends React.Component {
  state = { open: false, bowserInfo: '' }

   /**
   * Checks the orientation to make sure that the correct
   * hotzones are enabled, and adds a resize event listener.
   */
  componentDidMount() {
    window.addEventListener('resize', this.updateBowserInfo)
    this.updateBowserInfo()
  }

  /**
   * Will update round ID when receiving new response prop.
   * @param nextProps
   */
  componentWillReceiveProps = nextProps => {
    const { props, getRoundID } = this

    if (nextProps.response !== props.response) {
      getRoundID()
    }
  }

  /**
   * Removes the resize event listener.
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.updateBowserInfo)
  }

  updateBowserInfo = () => {
    this.setState({
      bowserInfo: JSON.stringify(bowser.detect(typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''))
    })
  }

  /**
   * Makes a "replay" request to the server, to get the previous roundID
   * from the response.
   * @returns {Promise<any>}
   */
  getRoundID = () => {
    const { props: { sid } } = this

    const url = `${config.SERVER_CONFIG.SERVER_URL}/replay/`
    const params = {
      sid,
      g: config.GAME_CONFIG.GAME_ID
    }

    return fetch(`${url}`, { method: 'POST', body: JSON.stringify(params) })
      .then(response => response.json())
      .then(replayInfo => {
        this.setState({ replayInfo })
        return replayInfo
      })
  }

  /**
   * Toggles the "step debug" option on or off.
   * @constructor
   */
  ToggleDebug = () => {
    const { stepDebug, setStepDebug } = this.props
    setStepDebug(!stepDebug)
  }

  /**
   * Toggles the disabling of restore state on or off.
   * If toggled off, the client will not send setRestoreState to
   * the server.
   * @constructor
   */
  ToggleRestore = () => {
    const { restoreStateDisabled, disableRestore, doSetRestoreState } = this.props
    doSetRestoreState({})
    disableRestore(!restoreStateDisabled)
  }

  /**
   * Toggles the hotzone component on or off.
   * @param {string} zone - The name of the hotzone to be toggled.
   * @constructor
   */
  ToggleHotzone = (zone) => {
    return () => this.props.doToggleHotzone(zone)
  }

  /**
   * Sends the client the same data as the previous round,
   * to have the whole round played again.
   * @constructor
   */
  RerunLastRound = () => {
    this.props.reRunLastRound()
  }

  /**
   * Initiates a download on the previous round data in
   * json format.
   */
  saveReplay = () => {
    const { state: { replayInfo }, downloadElement } = this

    if (!replayInfo || !replayInfo.roundId) {
      return
    }

    const blob = new Blob([ JSON.stringify(replayInfo) ], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    downloadElement.setAttribute('href', url)
    downloadElement.setAttribute('download', `${config.GAME_CONFIG.GAME_ID}[${replayInfo.roundId}].json`)
    downloadElement.click()
  }

  /**
   * Forwards the call to the appropriate action in order to enable gamespeed manipulation.
   */
  setGamespeed = (e) => {
    const val = (e && e.target && e.target.value) || e
    this.props.setGamespeed(Number(val))
  }

  /**
   * Toggles enabling of fullscreen requests on or off.
   */
  toggleFullscreen = () => {
    const { noFullscreen, doSetNoFullscreen } = this.props
    doSetNoFullscreen(!noFullscreen)
  }

  /**
   * Toggles the visibility of the FPS meter component.
   */
  toggleFPSmeter = () => {
    const { doToggleFPSmeter } = this.props
    doToggleFPSmeter()
  }

  /**
   * Toggles the visibility of the Recording controls component.
   */
  toggleRecordingControls = () => {
    const { doToggleRecordingControls } = this.props
    doToggleRecordingControls()
  }

  /**
   * Toggles the visibility of the exposed configurable variables GUI component.
   */
  toggleConfigurableVaraibles = () => {
    const { doToggleConfigurableVariables } = this.props
    doToggleConfigurableVariables()
  }

  /**
   * Toggles the visibility of the popup triggerer component.
   */
  togglePopupTriggerer = () => {
    const { doTogglePopupTriggerer } = this.props
    doTogglePopupTriggerer()
  }

  /**
   * Forwards the call to the appropriate action in order to change menu opacity.
   */
  setMenuOpacity = (e) => {
    const val = (e && e.target && e.target.value) || e
    this.props.setMenuOpacity(Number(val))
  }

  render() {
    const { replayInfo: { roundId = 0 } = {}, bowserInfo } = this.state

    const {
      stepDebug, restoreStateDisabled, hotzones, gamespeed, noFullscreen, hideFPSmeter,
      showRecordingControls, showConfigurableVariables, hasConfigurableVariables, userid,
      menuOpacity, showPopupTriggerer, sid,
    } = this.props

    return (
      <div>
        <div className="devConsolePage">
          <div className="devSelector">
            <a ref={e => this.downloadElement = e} style={{ display: 'none' }} />
            <button className="saveRoundButton" onClick={this.saveReplay} id="roundInfo" >
            { `Round ID: ${roundId}` }
            </button>
          </div>
          <div className="devSelector">
            <div className="volumeControl">
              <span>Menu Opacity: </span>
              <input type="range" onChange={this.setMenuOpacity} value={menuOpacity} min="0.1" max="1"
                step="0.01" />
              <span> { Math.round(menuOpacity * 100) }%</span><a style={{ cursor: 'pointer' }} onClick={() => this.setMenuOpacity(0.85)}>&nbsp;&nbsp;&#x21BA;</a>
            </div>
          </div>
          <div className="devSelector">
            <div className="volumeControl">
              <span>Gamespeed: </span>
              <input type="range" onChange={this.setGamespeed} value={gamespeed} min="0" max="3"
                step="0.01" />
              <span> { Math.round(gamespeed * 100) }%</span><a style={{ cursor: 'pointer' }} onClick={() => this.setGamespeed(1)}>&nbsp;&nbsp;&#x21BA;</a>
            </div>
          </div>
          <div className="devSelector">
            <input type="checkbox" checked={stepDebug} onChange={this.ToggleDebug}
              style={inputStyle} id="step" />

            <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="step">
              Enable stepping
            </label>
          </div>
          <div className="devSelector">
            <button onClick={this.RerunLastRound} style={inputStyle} id="replay">
              &#8635;
            </button>
            <label style={{ cursor: 'pointer', marginLeft: 10 }} htmlFor="replay">
              Re-run last round
            </label>
          </div>
          <div className="devSelector">
            <input type="checkbox" checked={restoreStateDisabled} onChange={this.ToggleRestore}
              style={inputStyle} id="restore" />

            <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="restore">
              Disable RestoreState
            </label>
          </div>
          <div className="devSelector">
            <input type="checkbox" checked={hotzones.ios} onChange={this.ToggleHotzone('ios')}
              style={inputStyle} id="iosHotzone" />
            <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="iosHotzone">
              Show iOS hotzone
            </label>
          </div>
          <div className="devSelector">
            <input type="checkbox" checked={hotzones.samsung} onChange={this.ToggleHotzone('samsung')}
              style={inputStyle} id="samsungHotzone" />
            <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="samsungHotzone">
              Show Samsung hotzone
            </label>
          </div>
          <div className="devSelector">
            <input type="checkbox" checked={!noFullscreen} onChange={this.toggleFullscreen}
              style={inputStyle} id="nofullscreen"/>

            <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="nofullscreen">
              Fullsceen enabled
            </label>
          </div>
          <div className="devSelector">
            <input type="checkbox" checked={!hideFPSmeter} onChange={this.toggleFPSmeter}
              style={inputStyle} id="noFPSmeter"/>

            <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="noFPSmeter">
              Show FPS meter
            </label>
          </div>
          { window.MediaRecorder && (
            <div className="devSelector">
              <input type="checkbox" checked={showRecordingControls} onChange={this.toggleRecordingControls}
                style={inputStyle} id="recordingcontrols"/>

              <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="recordingcontrols">
                Show recording controls
              </label>
            </div>
          )}
          { hasConfigurableVariables && (
            <div className="devSelector">
              <input type="checkbox" checked={showConfigurableVariables} onChange={this.toggleConfigurableVaraibles}
                style={inputStyle} id="configurableVariables"/>
              <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="configurableVariables">
                Show configurable variables
              </label>
            </div>
          )}
          <div className="devSelector">
            <input type="checkbox" checked={showPopupTriggerer} onChange={this.togglePopupTriggerer}
              style={inputStyle} id="popupTriggerer"/>

            <label style={{ marginLeft: 10, verticalAlign: 7, cursor: 'pointer' }} htmlFor="popupTriggerer">
              Show Popup Triggerer
            </label>
          </div>

        </div>
        <div className="bowser-info"><span>BOWSER INFO:</span> {bowserInfo}</div>
        <div style={{ padding: '10px' }}>MacGuffin version: {macGuffinPackage.version}{userid ? ` / userID: ${userid}` : ''}{sid ? ` / sessionID: ${sid}` : ''}</div>
      </div>
    )
  }
}
